// src/components/Admin/AdminDocumentos.jsx
import React, { useEffect, useMemo, useState } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Box, Button, Typography, Paper, Grid, TextField, Table, TableBody, TableCell,
    TableContainer, TableHead, TableRow, IconButton, Tooltip, Snackbar, Alert,
    LinearProgress, TablePagination
} from "@mui/material";
import { Delete, Download } from "@mui/icons-material";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import ImageIcon from "@mui/icons-material/Image";
import DescriptionIcon from "@mui/icons-material/Description";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import RefreshIcon from "@mui/icons-material/Refresh";
import axios from "axios";
import dayjs from "dayjs";
import "dayjs/locale/es";
import { useAuth } from "../../context/AuthContext";

dayjs.locale("es");

function iconForMime(mime) {
    if (!mime) return <InsertDriveFileIcon />;
    if (mime.startsWith("image/")) return <ImageIcon />;
    if (mime === "application/pdf") return <PictureAsPdfIcon />;
    if (mime.startsWith("text/")) return <DescriptionIcon />;
    return <InsertDriveFileIcon />;
}

// Normaliza snake_case -> camelCase (acepta ambas)
const normalizarDocumento = (d) => ({
    id: d.id,
    colegiadoCodigo: d.colegiadoCodigo ?? d.colegiado_codigo ?? null,
    nombreArchivo: d.nombreArchivo ?? d.nombre_archivo ?? "",
    tipoContenido: d.tipoContenido ?? d.tipo_contenido ?? "",
    fechaSubida: d.fechaSubida ?? d.fecha_subida ?? null,
});

export default function AdminDocumentos() {
    const API = process.env.REACT_APP_API_URL;
    const { token } = useAuth();
    const [searchParams] = useSearchParams();

    const [modo, setModo] = useState("");
    const [documentos, setDocumentos] = useState([]);
    const [total, setTotal] = useState(0);
    const [page, setPage] = useState(0);        // 0-based
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [sort, setSort] = useState("fechaSubida,desc");

    const [colegiadoId, setColegiadoId] = useState("");
    const [archivo, setArchivo] = useState(null);
    const [colegiadoSubida, setColegiadoSubida] = useState("");
    const [snack, setSnack] = useState({ open: false, msg: "", sev: "success" });
    const [loading, setLoading] = useState(false);

    const headers = useMemo(
        () => ({ Authorization: token ? `Bearer ${token}` : undefined }),
        [token]
    );

    const getPageMeta = (data) => ({
        content: Array.isArray(data?.content) ? data.content : [],
        totalElements: data?.totalElements ?? data?.total_elements ?? 0,
        number: data?.number ?? data?.page ?? 0,   // por si alguna lib renombra
        size: data?.size ?? data?.page_size ?? 0,
    });

    const fetchTodos = async () => {
        try {
            setLoading(true);
            const res = await axios.get(`${API}/api/documentos/todos`, {
                headers,
                params: { page, size: rowsPerPage, sort },
            });
            const meta = getPageMeta(res.data);
            const content = meta.content.map(normalizarDocumento);
            setDocumentos(content);
            setTotal(meta.totalElements);
            // opcional: sincroniza si el server reajusta page/size
            // setPage(meta.number);
            // setRowsPerPage(meta.size || rowsPerPage);
        } catch (err) {
            console.error(err);
            setDocumentos([]);
            setTotal(0);
            setSnack({ open: true, msg: "Error al cargar el listado global.", sev: "error" });
        } finally {
            setLoading(false);
        }
    };

    const fetchPorColegiado = async () => {
        if (!colegiadoId) return;
        try {
            setLoading(true);
            const res = await axios.get(`${API}/api/documentos/colegiado/${colegiadoId}`, {
                headers,
                params: { page, size: rowsPerPage, sort },
            });
            const meta = getPageMeta(res.data);
            const content = meta.content.map(normalizarDocumento);
            setDocumentos(content);
            setTotal(meta.totalElements);
            // setPage(meta.number);
            // setRowsPerPage(meta.size || rowsPerPage);
        } catch (err) {
            console.error(err);
            setDocumentos([]);
            setTotal(0);
            setSnack({ open: true, msg: "Error al cargar documentos del colegiado.", sev: "error" });
        } finally {
            setLoading(false);
        }
    };

    // Deep-link: /admin/documentos?colegiado=XXXX  → abrir en modo porColegiado
    useEffect(() => {
        const q = (searchParams.get("colegiado") || "").trim();
        if (q) {
            setModo("porColegiado");
            setColegiadoId(q);
            setPage(0);
        }
    }, [searchParams]);

    // Carga automática según modo/paginación/orden
    useEffect(() => {
        if (modo === "todos") fetchTodos();
        if (modo === "porColegiado" && colegiadoId) fetchPorColegiado();
    }, [modo, page, rowsPerPage, sort]);

    const handleFirmarPDF = async (e) => {
        const file = e.target.files?.[0];
        if (!file) return;

        const formData = new FormData();
        // el nombre del campo DEBE ser "file" (igual que en @RequestParam("file"))
        formData.append("file", file);

        try {
            const res = await axios.post(`${API}/pdf/firmar`, formData, {
                responseType: "blob",
                headers: {
                    ...(headers || {}),
                    "Content-Type": "multipart/form-data",
                },
            });

            const blob = new Blob([res.data], { type: "application/pdf" });
            const url = window.URL.createObjectURL(blob);
            const a = document.createElement("a");
            a.href = url;
            a.download = file.name.replace(/\.pdf$/i, "_firmado.pdf");
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (error) {
            console.error("Error al firmar PDF:", error);
            setSnack({ open: true, msg: "No se pudo firmar el documento.", sev: "error" });
        } finally {
            e.target.value = null;
        }
    };


    const descargar = async (id, nombreArchivo) => {
        try {
            const res = await axios.get(`${API}/api/documentos/descargar/${id}`, {
                responseType: "blob",
                headers,
            });
            const cd = res.headers["content-disposition"] || "";
            const match = /filename="?([^"]+)"?$/i.exec(cd);
            const filename = match?.[1] || nombreArchivo || `documento_${id}`;

            const url = window.URL.createObjectURL(new Blob([res.data]));
            const a = document.createElement("a");
            a.href = url;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            a.remove();
            window.URL.revokeObjectURL(url);
        } catch (err) {
            console.error("Error al descargar archivo:", err);
            setSnack({ open: true, msg: "No se pudo descargar el archivo.", sev: "error" });
        }
    };

    const eliminar = async (id) => {
        if (!window.confirm("¿Seguro que deseas eliminar este documento?")) return;
        try {
            await axios.delete(`${API}/api/documentos/${id}`, { headers });

            // Si eliminamos el último de la página y no es la primera, retrocede una página
            if (documentos.length === 1 && page > 0) {
                setPage(page - 1);
                return; // el useEffect hará el fetch
            }
            // si no, refresca la página actual
            if (modo === "todos") fetchTodos();
            if (modo === "porColegiado" && colegiadoId) fetchPorColegiado();

            setSnack({ open: true, msg: "Documento eliminado.", sev: "success" });
        } catch (err) {
            console.error(err);
            setSnack({ open: true, msg: "Error al eliminar documento.", sev: "error" });
        }
    };

    const subirDocumento = async () => {
        if (!archivo || !colegiadoSubida) return;
        const formData = new FormData();
        formData.append("file", archivo);
        formData.append("colegiadoId", colegiadoSubida);

        try {
            await axios.post(`${API}/api/documentos/admin/subir`, formData, {
                headers: { ...headers, "Content-Type": "multipart/form-data" },
            });
            setSnack({ open: true, msg: "Documento subido correctamente.", sev: "success" });
            setArchivo(null);
            setColegiadoSubida("");
            // refrescar listado actual
            if (modo === "todos") fetchTodos();
            if (modo === "porColegiado" && colegiadoId) fetchPorColegiado();
        } catch (err) {
            console.error(err);
            setSnack({ open: true, msg: "Error al subir documento.", sev: "error" });
        }
    };

    const handleChangePage = (_e, newPage) => setPage(newPage);
    const handleChangeRowsPerPage = (e) => {
        setRowsPerPage(parseInt(e.target.value, 10));
        setPage(0);
    };

    return (
        <Paper sx={{ padding: 3, margin: 2 }}>
            <Typography variant="h5" gutterBottom>
                Gestión de Documentos de Colegiados
            </Typography>

            <Grid container spacing={2} sx={{ marginBottom: 2 }}>
                <Grid item>
                    <Button variant="contained" onClick={() => { setModo("porColegiado"); setPage(0); }}>
                        Ver por colegiado
                    </Button>
                </Grid>
                <Grid item>
                    <Button
                        variant="contained"
                        onClick={() => { setModo("todos"); setPage(0); }}
                        startIcon={<RefreshIcon />}
                    >
                        Ver todos los documentos
                    </Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" onClick={() => setModo("subir")}>
                        Subir documento a colegiado
                    </Button>
                </Grid>
                <Grid item>
                    <Button variant="contained" component="label" startIcon={<PictureAsPdfIcon />}>
                        Firmar PDF
                        <input hidden type="file" accept=".pdf" onChange={handleFirmarPDF} />
                    </Button>
                </Grid>
            </Grid>

            {modo === "porColegiado" && (
                <Box>
                    <TextField
                        label="Código de Colegiado"
                        value={colegiadoId}
                        onChange={(e) => setColegiadoId(e.target.value)}
                        sx={{ marginRight: 2 }}
                        size="small"
                    />
                    <Button variant="outlined" onClick={() => { setPage(0); fetchPorColegiado(); }}>
                        Buscar
                    </Button>
                </Box>
            )}

            {modo === "subir" && (
                <Box sx={{ mt: 2 }}>
                    <TextField
                        label="Código Colegiado"
                        value={colegiadoSubida}
                        onChange={(e) => setColegiadoSubida(e.target.value)}
                        sx={{ marginRight: 2 }}
                        size="small"
                    />
                    <Button variant="contained" component="label">
                        Seleccionar Archivo
                        <input hidden type="file" onChange={(e) => setArchivo(e.target.files?.[0] || null)} />
                    </Button>
                    <Button variant="outlined" onClick={subirDocumento} sx={{ marginLeft: 2 }}>
                        Subir
                    </Button>
                </Box>
            )}

            {loading && <LinearProgress sx={{ mt: 2 }} />}

            {modo && (modo === "todos" || (modo === "porColegiado" && colegiadoId)) && (
                <>
                    <TableContainer sx={{ mt: 3 }}>
                        <Table size="small">
                            <TableHead>
                                <TableRow>
                                    <TableCell>ID</TableCell>
                                    <TableCell>Tipo</TableCell>
                                    <TableCell>Nombre</TableCell>
                                    <TableCell>Contenido</TableCell>
                                    <TableCell>Colegiado</TableCell>
                                    <TableCell>Fecha subida</TableCell>
                                    <TableCell align="right">Acciones</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {documentos.map((doc) => (
                                    <TableRow key={doc.id}>
                                        <TableCell>{doc.id}</TableCell>
                                        <TableCell width={48}>{iconForMime(doc.tipoContenido)}</TableCell>
                                        <TableCell sx={{ wordBreak: "break-all" }}>{doc.nombreArchivo || "-"}</TableCell>
                                        <TableCell>{doc.tipoContenido || "-"}</TableCell>
                                        <TableCell>{doc.colegiadoCodigo ?? "-"}</TableCell>
                                        <TableCell>
                                            {doc.fechaSubida ? dayjs(doc.fechaSubida).format("DD/MM/YYYY HH:mm") : "-"}
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Descargar">
                                                <IconButton onClick={() => descargar(doc.id, doc.nombreArchivo)} size="small">
                                                    <Download />
                                                </IconButton>
                                            </Tooltip>
                                            <Tooltip title="Eliminar">
                                                <IconButton onClick={() => eliminar(doc.id)} size="small" color="error">
                                                    <Delete />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                ))}
                                {!loading && documentos.length === 0 && (
                                    <TableRow>
                                        <TableCell colSpan={7}>
                                            <Typography variant="body2" color="text.secondary">
                                                No hay documentos para mostrar.
                                            </Typography>
                                        </TableCell>
                                    </TableRow>
                                )}
                            </TableBody>
                        </Table>
                    </TableContainer>

                    <TablePagination
                        component="div"
                        count={total}
                        page={page}
                        onPageChange={handleChangePage}
                        rowsPerPage={rowsPerPage}
                        onRowsPerPageChange={handleChangeRowsPerPage}
                        rowsPerPageOptions={[5, 10, 25, 50, 100]}
                        labelRowsPerPage="Filas por página"
                    />
                </>
            )}

            <Snackbar
                open={snack.open}
                autoHideDuration={4000}
                onClose={() => setSnack({ ...snack, open: false })}
                anchorOrigin={{ vertical: "bottom", horizontal: "center" }}
            >
                <Alert
                    onClose={() => setSnack({ ...snack, open: false })}
                    severity={snack.sev}
                    variant="filled"
                    sx={{ width: "100%" }}
                >
                    {snack.msg}
                </Alert>
            </Snackbar>
        </Paper>
    );
}
